import React, { useState, useEffect } from "react";
import {
  BasePageLayout,
  BaseBox,
  BaseTypography,
  BaseButton,
  BaseTextField,
} from "../../Common/UI/exports";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { fetchRepositories } from "../../APIs/AzureAPI";
import { loadReleaseRequestByKey } from "../../APIs/operationAPI";

const cache = { repositories: null };

function GitFlowReleaseManagement() {
  const navigate = useNavigate();
  const [repositories, setRepositories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [operationId, setOperationId] = useState("");

  useEffect(() => {
    const loadRepositories = async () => {
      if (cache.repositories) {
        setRepositories(cache.repositories);
      } else {
        try {
          const data = await fetchRepositories();
          const initializedData = data.map((repo) => ({
            ...repo,
            selected: false,
            priorityGroup: "",
          }));
          setRepositories(initializedData);
          cache.repositories = initializedData;
        } catch (error) {
          console.error("Error loading repositories:", error);
        }
      }
    };
    loadRepositories();
  }, []);

  const handleCheckboxChange = (index) => {
    setRepositories((prev) =>
      prev.map((repo, i) =>
        i === index ? { ...repo, selected: !repo.selected } : repo
      )
    );
  };

  const handlePriorityChange = (index, event) => {
    const value = event.target.value;
    setRepositories((prev) =>
      prev.map((repo, i) =>
        i === index ? { ...repo, priorityGroup: value } : repo
      )
    );
  };

  const normalizePriorities = (repos) => {
    const selected = repos.filter((r) => r.selected);
    const sorted = selected.sort(
      (a, b) => (a.priorityGroup || 0) - (b.priorityGroup || 0)
    );
    const map = new Map();
    let current = 1;
    sorted.forEach((r) => {
      if (!map.has(r.priorityGroup)) {
        map.set(r.priorityGroup, current);
        current++;
      }
    });
    return repos.map((r) =>
      r.selected
        ? { ...r, priorityGroup: map.get(r.priorityGroup).toString() }
        : r
    );
  };

  const handleNext = () => {
    const anySelected = repositories.some((r) => r.selected);
    if (!anySelected) {
      setModalMessage("No selected repositories to display.");
      setShowModal(true);
      return;
    }

    const allHavePriority = repositories
      .filter((r) => r.selected)
      .every((r) => r.priorityGroup !== "");
    if (!allHavePriority) {
      setModalMessage(
        "Please select a priority group for all selected repositories."
      );
      setShowModal(true);
      return;
    }

    const normalized = normalizePriorities(repositories);
    const opId = uuidv4();
    navigate("/priority-groups", {
      state: { repositories: normalized, operationId: opId },
    });
  };

  const handleBack = () => navigate(-1);
  const handleLoad = () => setShowLoadModal(true);

  const handleLoadSubmit = async () => {
    try {
      const response = await loadReleaseRequestByKey(operationId);
      const loadedRepositories = response.repositories;

      const updatedRepositories = cache.repositories
        .filter((repo) =>
          loadedRepositories.some(
            (loaded) => loaded.repositoryName === repo.name
          )
        )
        .map((repo) => {
          const match = loadedRepositories.find(
            (loaded) => loaded.repositoryName === repo.name
          );
          return { ...repo, selected: true, priorityGroup: match.priorityGroup };
        });

      setRepositories(updatedRepositories);
      const normalized = normalizePriorities(updatedRepositories);
      navigate("/priority-groups", {
        state: { repositories: normalized, operationId, loaded: true },
      });
      setShowLoadModal(false);
    } catch (error) {
      console.error("Error loading release request:", error);
      setModalMessage("Error loading release request.");
      setShowModal(true);
    }
  };

  const handleCancelLoad = () => {
    setOperationId("");
    setShowLoadModal(false);
  };

  return (
    <BasePageLayout sidebarItems={[]}>
      <BaseBox
        sx={{
          height: "calc(100vh - 140px)", 
          display: "flex",
          flexDirection: "column",
          maxWidth: "1160px",
          mx: "auto",
          mt: 0,
          px: 2,
        }}
      >
        <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
          GitFlow Release / Hotfix Management
        </BaseTypography>

        {/* Table Container */}
        <BaseBox
          sx={{
            flexGrow: 1, // fill available height
            overflowY: "auto",
            border: "1px solid #ccc",
          }}
        >
          <table className="repositories-table" style={{ width: "100%" }}>
            <thead style={{ backgroundColor: "#c4001d", color: "#fff" }}>
              <tr>
                <th style={{ padding: "8px", color: "#040404ff" }}>Name</th>
                <th style={{ padding: "8px", color: "#040404ff" }}>Select</th>
                <th style={{ padding: "8px", color: "#040404ff" }}>Priority Group</th>
              </tr>
            </thead>
            <tbody>
              {repositories.map((repo, index) => (
                <tr key={repo.id}>
                  <td style={{ padding: "8px" }}>{repo.name}</td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={repo.selected}
                      onChange={() => handleCheckboxChange(index)}
                    />
                  </td>
                  <td style={{ padding: "8px" }}>
                    <select
                      value={repo.priorityGroup}
                      onChange={(e) => handlePriorityChange(index, e)}
                      disabled={!repo.selected}
                    >
                      <option value="">Select Priority</option>
                      {[1, 2, 3, 4, 5, 6].map((num) => (
                        <option key={num} value={num}>
                          {num === 1
                            ? "First"
                            : num === 2
                            ? "Second"
                            : num === 3
                            ? "Third"
                            : num === 4
                            ? "Fourth"
                            : num === 5
                            ? "Fifth"
                            : "Last"}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </BaseBox>

        {/* Buttons below table */}
        <BaseBox sx={{ display: "flex", gap: 2, mt: 2 }}>
          <BaseButton onClick={handleBack} color="secondary">
            Back
          </BaseButton>
          <BaseButton onClick={handleLoad} color="info">
            Load
          </BaseButton>
          <BaseButton onClick={handleNext} color="primary">
            Next
          </BaseButton>
        </BaseBox>

        {/* Modal for alerts */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <p>{modalMessage}</p>
              <button onClick={() => setShowModal(false)}>OK</button>
            </div>
          </div>
        )}

        {/* Modal for loading operation */}
        {showLoadModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <label htmlFor="operation-id">Operation ID:</label>
              <BaseTextField
                id="operation-id"
                value={operationId}
                onChange={(e) => setOperationId(e.target.value)}
              />
              <BaseBox sx={{ display: "flex", gap: 2, mt: 2 }}>
                <BaseButton onClick={handleLoadSubmit} color="primary">
                  OK
                </BaseButton>
                <BaseButton onClick={handleCancelLoad} color="secondary">
                  Cancel
                </BaseButton>
              </BaseBox>
            </div>
          </div>
        )}
      </BaseBox>
    </BasePageLayout>
  );
}

export default GitFlowReleaseManagement;
